"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { X, AlertCircle } from "lucide-react";
import api from "@/lib/api";

interface SoftLaunchSettings {
  "soft_launch.disable_signup": boolean;
  "soft_launch.disable_browsing": boolean;
  "soft_launch.disable_booking": boolean;
  "soft_launch.early_access_message"?: string;
}

export default function SoftLaunchBanner() {
  const [settings, setSettings] = useState<SoftLaunchSettings | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get("/public/platform-settings/soft-launch");
        setSettings(response.data);
      } catch (err) {
        console.error("Failed to load soft launch settings:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();

    // Check if banner was dismissed
    const dismissedKey = localStorage.getItem("soft_launch_banner_dismissed");
    if (dismissedKey === "true") {
      setDismissed(true);
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem("soft_launch_banner_dismissed", "true");
  };

  if (loading || dismissed || !settings) {
    return null;
  }

  // Check if any soft launch feature is enabled
  const isSoftLaunchActive =
    settings["soft_launch.disable_signup"] ||
    settings["soft_launch.disable_browsing"] ||
    settings["soft_launch.disable_booking"];

  if (!isSoftLaunchActive) {
    return null;
  }

  const message = settings["soft_launch.early_access_message"] ||
    "Pacific North North is currently in early access. Join our Discord to get updates!";

  return (
    <div
      className="relative bg-gradient-to-r from-[#0A3740] to-[#0E4A52] text-white py-3 px-4 border-b"
      style={{ borderColor: "rgba(212, 175, 55, 0.4)" }}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <AlertCircle className="h-5 w-5 flex-shrink-0" style={{ color: "#D4AF37" }} />
          <p className="text-sm font-medium flex-1">
            {message}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            href="https://discord.gg/pacificnorthnorth"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: "#D4AF37", color: "#0A3740" }}
          >
            Join Discord
          </Link>
          <button
            onClick={handleDismiss}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Dismiss banner"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

